import { sharedLib } from "@shared/services/shared.service.ts";
import React, { ChangeEvent, useEffect, useState } from "react";
import { GroupData, LanguageNameSpaces, SubGroupType } from "@shared/types";
import { displayResultMessage } from "utils/misc.tsx";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import Input from "common/Inputs/Input.tsx";
import { typography } from "@shared/common/Themes/typography.ts";
import RequiredFieldMessage from "common/Animations/RequiredFieldMessage.tsx";

interface usePersonalGroupModalProps {
  group?: GroupData;
  subGroupType?: SubGroupType;
  onSuccess?: () => void;
}

const usePersonalGroupModal = ({
  group,
  subGroupType,
  onSuccess,
}: usePersonalGroupModalProps) => {
  const [createSubGroup, { isLoading: createLoading }] =
    sharedLib.sharedApi.settings.useCreateSubGroupMutation();
  const [editSubGroup, { isLoading: editLoading }] =
    sharedLib.sharedApi.settings.useEditSubGroupMutation();
  const { t } = useTypedTranslation(LanguageNameSpaces.GROUPS);

  const [name, setName] = useState(group?.name || "");
  const [showError, setShowError] = useState(false);

  const isEdit = !!group;
  const isPersonal = subGroupType === SubGroupType.PERSONAL;
  const maxLength = name.length > MAX_NAME_LENGTH;
  const isDisabled = !name.length || maxLength;

  useEffect(() => {
    if (group && !name.length) {
      setName(group?.name);
    }
  }, [group]);

  useEffect(() => {
    if (showError && !isDisabled) {
      setShowError(false);
    }
  }, [showError, isDisabled]);

  const onOkClick = () => {
    if (isDisabled) {
      setShowError(true);
      return;
    }
    const action = isEdit ? editSubGroup : createSubGroup;
    const args = isEdit
      ? { name, sub_group_id: group.id }
      : { name, sub_group_type: subGroupType };
    const translationObject = {
      group_type: isPersonal ? "personal" : "Cornix",
    };
    displayResultMessage(
      action(args as any).unwrap(),
      isEdit
        ? t("modal.success_edited", translationObject)
        : t("modal.success_created", translationObject)
    )
      .then(() => {
        onSuccess?.();
        setName("");
      })
      .catch(() => {});
  };

  const getErrorMessage = () => {
    if (!name.length && showError) {
      return t("modal.name_required");
    } else if (maxLength) {
      return t("modal.name_too_long", { max: MAX_NAME_LENGTH });
    }
    return "";
  };

  const nameInput = (
    <Input
      autoFocus
      value={name}
      onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
      style={{ maxWidth: 500 }}
    />
  );

  const errorMessage = (
    <RequiredFieldMessage
      requirement={isDisabled}
      errorMessage={getErrorMessage()}
      style={{ fontSize: typography.fontSizes.S, marginTop: 4 }}
    />
  );

  return {
    isLoading: createLoading || editLoading,
    isDisabled,
    onOkClick,
    nameInput,
    errorMessage,
  };
};

export default usePersonalGroupModal;

const MAX_NAME_LENGTH = 32;
